import React, { useEffect, useState } from 'react';
import './Sessions.css';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Chip,
  IconButton,
  Popover,
  InputAdornment,
  InputBase,
  ClickAwayListener,
  Paper,
  Radio,
  Checkbox,
  Fade,
} from '@mui/material';
import Layout from '../../../components/Layout';
import TeacherNavbar from '../../../components/TeacherNavbar/TeacherNavbar';
import PropTypes from 'prop-types';
import SessionTable from '../../../components/SessionTable/SessionTable';
import { axiosInstance } from '../../../utils/tokenUtils';
import { useTranslation } from '../../../i18n/LanguageContext';
import CircularProgress from '@mui/material/CircularProgress';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs from 'dayjs';
import ClearIcon from '@mui/icons-material/Clear';
import DropdownIcon from '../../../assets/ScheduleIcon/dropdown.svg';
import FilterListIcon from '../../../assets/FiltersIcon/filter.svg';
import SearchIcon from '../../../assets/FiltersIcon/search.svg';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarMonthIcon from '../../../assets/ScheduleIcon/calendar.svg';
import LoadingScreen from '../../../components/LoadingIcon/LoadingScreen';
import usePagination from '@mui/material/usePagination';
import { styled } from '@mui/material/styles';

const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '10px 0',
  display: 'flex',
  justifyContent: 'center',
});

function formatText(input) {
  if (!input) return '---';
  return input
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
}

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3, pb: 0 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// Helper function to convert UTC timestamps to local timezone
const convertToLocalTime = sessions => {
  return sessions.map(session => {
    // Create a local timezone version of each timestamp
    const startTimeUTC = session.start_time ? new Date(session.start_time) : null;
    const startTimeLocal = startTimeUTC
      ? new Date(startTimeUTC.getTime() - startTimeUTC.getTimezoneOffset() * 60000)
      : null;

    // Handle other date fields similarly
    const cancelledAtUTC = session.cancelled_at ? new Date(session.cancelled_at) : null;
    const cancelledAtLocal = cancelledAtUTC
      ? new Date(cancelledAtUTC.getTime() - cancelledAtUTC.getTimezoneOffset() * 60000)
      : null;

    const createdAtUTC = session.created_at ? new Date(session.created_at) : null;
    const createdAtLocal = createdAtUTC
      ? new Date(createdAtUTC.getTime() - createdAtUTC.getTimezoneOffset() * 60000)
      : null;

    const updatedAtUTC = session.updated_at ? new Date(session.updated_at) : null;
    const updatedAtLocal = updatedAtUTC
      ? new Date(updatedAtUTC.getTime() - updatedAtUTC.getTimezoneOffset() * 60000)
      : null;

    return {
      ...session,
      start_time: startTimeLocal ? startTimeLocal.toISOString() : null,
      cancelled_at: cancelledAtLocal ? cancelledAtLocal.toISOString() : null,
      created_at: createdAtLocal ? createdAtLocal.toISOString() : null,
      updated_at: updatedAtLocal ? updatedAtLocal.toISOString() : null,
    };
  });
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Level options based on teacher type
const LEVEL_OPTIONS = {
  academic: [
    { value: 'elementary', label: 'Elementary' },
    { value: 'middle_school', label: 'Middle School' },
    { value: 'high_school', label: 'High School' },
    { value: 'university', label: 'University' },
  ],
  creative: [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'professional', label: 'Professional' },
  ],
};

// Session type options
const SESSION_TYPE_OPTIONS = [
  { value: 'one_on_one', label: 'One-on-One' },
  { value: 'group', label: 'Group' },
  { value: 'prime', label: 'Prime' },
];

const Sessions = () => {
  const [value, setValue] = useState(0);
  const [upcomingSessionData, setUpcomingSessionData] = useState([]);
  const [completedSessionData, setCompletedSessionData] = useState([]);
  const [cancelledSessionData, setCancelledSessionData] = useState([]);
  const { translate } = useTranslation();

  // Pagination states
  const [upcomingPagination, setUpcomingPagination] = useState({
    count: 0,
    offset: 0,
    limit: 10,
  });
  const [completedPagination, setCompletedPagination] = useState({
    count: 0,
    offset: 0,
    limit: 10,
  });
  const [cancelledPagination, setCancelledPagination] = useState({
    count: 0,
    offset: 0,
    limit: 10,
  });

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState('');
  const [selectedLevels, setSelectedLevels] = useState('');
  const [selectedSessionTypes, setSelectedSessionTypes] = useState('');
  const [selectedTags, setSelectedTags] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  // Calendar popover state
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const openCalendar = Boolean(calendarAnchorEl);

  // Add these state variables at the top of the Sessions component
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [subjectsList, setSubjectsList] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);

  // Subjects dropdown pagination state
  const [subjectsPage, setSubjectsPage] = useState(1);
  const SUBJECTS_PAGE_SIZE = 30;
  const [subjectsTotalCount, setSubjectsTotalCount] = useState(0);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  // Tags dropdown pagination state
  const [tagsPage, setTagsPage] = useState(1);
  const TAGS_PAGE_SIZE = 40;
  const [tagsTotalCount, setTagsTotalCount] = useState(0);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Clear filters function
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDate(null);
    setSelectedSubjects('');
    setSelectedLevels('');
    setSelectedSessionTypes('');
    setSelectedTags('');
    setCurrentMonth(dayjs());
  };

  // Helper to compose sessions query URL with current filters
  const buildSessionsUrl = (status, offset = 0, limit = 10) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('offset', String(offset));
    params.set('limit', String(limit));

    // Month (YYYY-MM) - based on currentMonth state
    if (currentMonth) {
      params.set('month', dayjs(currentMonth).format('YYYY-MM'));
    }

    // Date (YYYY-MM-DD)
    if (selectedDate) {
      params.set('date', dayjs(selectedDate).format('YYYY-MM-DD'));
    }

    if (selectedSessionTypes) params.set('session_type', selectedSessionTypes);
    if (selectedLevels) params.set('level_of_teaching', selectedLevels);
    if (selectedSubjects) params.set('subject', selectedSubjects);
    if (selectedTags) params.set('tags', selectedTags);

    return `/teachers/sessions?${params.toString()}`;
  };

  const getUpcomingSessionData = async (offset = 0, limit = 10) => {
    try {
      const { data } = await axiosInstance.get(buildSessionsUrl('upcoming', offset, limit));
      const convertedData = convertToLocalTime(data.results || data);
      setUpcomingSessionData(convertedData);
      setUpcomingPagination({
        count: data.count || 0,
        offset: data.offset || 0,
        limit: data.limit || 10,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const getCompletedSessionData = async (offset = 0, limit = 10) => {
    console.log('getCompletedSessionData called');
    try {
      const { data } = await axiosInstance.get(buildSessionsUrl('completed', offset, limit));
      console.log('Completed sessions API response:', data);
      const convertedData = convertToLocalTime(data.results || data);
      console.log('Converted completed sessions:', convertedData);
      setCompletedSessionData(convertedData);
      setCompletedPagination({
        count: data.count || 0,
        offset: data.offset || 0,
        limit: data.limit || 10,
      });
    } catch (error) {
      console.log('Error fetching completed sessions:', error);
    }
  };

  const getCancelledSessionData = async (offset = 0, limit = 10) => {
    try {
      const { data } = await axiosInstance.get(buildSessionsUrl('cancelled', offset, limit));
      const convertedData = convertToLocalTime(data.results || data);
      setCancelledSessionData(convertedData);
      setCancelledPagination({
        count: data.count || 0,
        offset: data.offset || 0,
        limit: data.limit || 10,
      });
    } catch (error) {
      console.log(error);
    }
  };

  // Add these functions to fetch data
  const getTeacherProfile = async () => {
    try {
      const { data } = await axiosInstance.get('/teachers');
      setTeacherProfile(data);
    } catch (error) {
      console.log(error);
    }
  };

  const getSubjects = async (type, page = 1, size = SUBJECTS_PAGE_SIZE, append = true) => {
    if (!type) return;
    try {
      setSubjectsLoading(true);
      const { data } = await axiosInstance.get(
        `/common/subjects?subject_type=${type}&page=${page}&size=${size}`
      );

      const results = Array.isArray(data?.results) ? data.results : [];

      setSubjectsTotalCount(typeof data?.count === 'number' ? data.count : results.length);
      setSubjectsPage(page);

      if (append) {
        // Append while de-duplicating by id
        setSubjectsList(prev => {
          const existingIds = new Set(prev.map(s => s.id));
          const merged = [...prev];
          results.forEach(item => {
            if (!existingIds.has(item.id)) merged.push(item);
          });
          return merged;
        });
      } else {
        setSubjectsList(results);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const hasMoreSubjects = subjectsList.length < subjectsTotalCount;

  const handleSubjectsMenuScroll = event => {
    const target = event?.currentTarget;
    if (!target) return;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
    if (nearBottom && !subjectsLoading && hasMoreSubjects) {
      const nextPage = subjectsPage + 1;
      getSubjects(teacherProfile?.teacher_type, nextPage, SUBJECTS_PAGE_SIZE, true);
    }
  };

  const getTags = async (subjectId, page = 1, size = TAGS_PAGE_SIZE, append = true) => {
    if (!subjectId) return;
    try {
      setTagsLoading(true);
      const { data } = await axiosInstance.get(
        `/common/${subjectId}/tags?page=${page}&page_size=${size}`
      );

      const results = Array.isArray(data?.results) ? data.results : [];

      setTagsTotalCount(typeof data?.count === 'number' ? data.count : results.length);
      setTagsPage(page);

      if (append) {
        setTagsList(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const merged = [...prev];
          results.forEach(item => {
            if (!existingIds.has(item.id)) merged.push(item);
          });
          return merged;
        });
      } else {
        setTagsList(results);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setTagsLoading(false);
    }
  };

  const hasMoreTags = tagsList.length < tagsTotalCount;

  const handleTagsMenuScroll = event => {
    const target = event?.currentTarget;
    if (!target) return;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
    if (nearBottom && !tagsLoading && hasMoreTags) {
      const nextPage = tagsPage + 1;
      const selectedSubject = subjectsList.find(subject => subject.title === selectedSubjects);
      if (selectedSubject?.id) {
        getTags(selectedSubject.id, nextPage, TAGS_PAGE_SIZE, true);
      }
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setFadeIn(false);

      try {
        // Only fetch upcoming sessions initially (tab 0)
        await getUpcomingSessionData();
        await getTeacherProfile();

        setLoading(false);

        // Delay fade in for a smoother transition
        setTimeout(() => {
          setFadeIn(true);
        }, 100);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
        setFadeIn(true);
      }
    };

    fetchInitialData();
  }, []);

  // Load data when tab changes
  useEffect(() => {
    const loadTabData = async () => {
      switch (value) {
        case 0:
          await getUpcomingSessionData(0, upcomingPagination.limit || 10);
          break;
        case 1:
          await getCompletedSessionData(0, completedPagination.limit || 10);
          break;
        case 2:
          await getCancelledSessionData(0, cancelledPagination.limit || 10);
          break;
      }
    };
    loadTabData();
  }, [value]);

  // Refetch when any filter changes
  useEffect(() => {
    const reloadForFilters = async () => {
      switch (value) {
        case 0:
          await getUpcomingSessionData(0, upcomingPagination.limit || 10);
          break;
        case 1:
          await getCompletedSessionData(0, completedPagination.limit || 10);
          break;
        case 2:
          await getCancelledSessionData(0, cancelledPagination.limit || 10);
          break;
      }
    };
    reloadForFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDate,
    selectedLevels,
    selectedSessionTypes,
    selectedSubjects,
    selectedTags,
    currentMonth,
  ]);

  useEffect(() => {
    if (teacherProfile) {
      // Reset and fetch first page of subjects when teacher type is available/changes
      setSubjectsList([]);
      setSubjectsTotalCount(0);
      setSubjectsPage(1);
      getSubjects(teacherProfile.teacher_type, 1, SUBJECTS_PAGE_SIZE, false);
    }
  }, [teacherProfile]);

  const handleChange = (event, newValue) => {
    console.log('handleChange called with newValue:', newValue);
    setValue(newValue);
  };

  const handleCalendarClick = event => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  const handleDateChange = date => {
    setSelectedDate(date);
    setCurrentMonth(date);
    handleCalendarClose();
  };

  const handlePrevMonth = () => {
    const newMonth = currentMonth.subtract(1, 'month');
    setCurrentMonth(newMonth);
    setSelectedDate(null); // Clear specific date selection when navigating months
  };

  const handleNextMonth = () => {
    const newMonth = currentMonth.add(1, 'month');
    setCurrentMonth(newMonth);
    setSelectedDate(null); // Clear specific date selection when navigating months
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleClickAway = () => {
    setShowFilters(false);
  };

  // Get current month name to display
  const getCurrentMonthYear = () => {
    return currentMonth.format('MMM YYYY');
  };

  // Determine if currentMonth is different from the default (this month)
  const isDefaultMonth = dayjs(currentMonth).isSame(dayjs(), 'month');

  // Pagination handlers
  const handlePageChange = (newPage, paginationType) => {
    const offset = (newPage - 1) * (paginationType.limit || 10);

    switch (value) {
      case 0: // Upcoming
        getUpcomingSessionData(offset, paginationType.limit);
        break;
      case 1: // Completed
        getCompletedSessionData(offset, paginationType.limit);
        break;
      case 2: // Cancelled
        getCancelledSessionData(offset, paginationType.limit);
        break;
    }
  };

  const handleRowsPerPageChange = (newLimit, paginationType) => {
    switch (value) {
      case 0: // Upcoming
        getUpcomingSessionData(0, newLimit);
        break;
      case 1: // Completed
        getCompletedSessionData(0, newLimit);
        break;
      case 2: // Cancelled
        getCancelledSessionData(0, newLimit);
        break;
    }
  };

  // Get current pagination data based on active tab
  const getCurrentPagination = () => {
    switch (value) {
      case 0:
        return upcomingPagination;
      case 1:
        return completedPagination;
      case 2:
        return cancelledPagination;
      default:
        return upcomingPagination;
    }
  };

  // Get current session data based on active tab
  const getCurrentSessionData = () => {
    switch (value) {
      case 0:
        return upcomingSessionData;
      case 1:
        return completedSessionData;
      case 2:
        return cancelledSessionData;
      default:
        return upcomingSessionData;
    }
  };

  // Calculate pagination values
  const currentPagination = getCurrentPagination();
  const currentPage = Math.floor(currentPagination.offset / currentPagination.limit) + 1;
  const totalPages = Math.ceil(currentPagination.count / currentPagination.limit);

  // Move usePagination hook to the top level
  const { items } = usePagination({
    count: totalPages,
    page: currentPage,
    onChange: (event, value) => handlePageChange(value, currentPagination),
  });

  // Add a loading check at the beginning of the render
  if (loading) {
    return (
      <Layout title="Klass Online | Sessions">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <TeacherNavbar />
            <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px] flex justify-center items-center">
              <LoadingScreen />
            </Box>
          </Box>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Klass Online | Sessions">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <TeacherNavbar />
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <Fade in={fadeIn} timeout={800}>
              <Box className="flex flex-col p-[1rem] gap-3">
                {/* Header with Title and Search */}
                <Box className="flex flex-col gap-4">
                  <Box className="flex justify-between items-center">
                    <Typography className="session-title">
                      {translate('session', 'sessionOverviewText')}
                    </Typography>

                    <Box className="flex items-center gap-3">
                      {/* Search Bar */}
                      <Box className="search-container">
                        <InputBase
                          placeholder="Search Name"
                          className="search-input"
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          startAdornment={
                            <InputAdornment position="start">
                              <img src={SearchIcon} alt="Search" className="w-4 h-4 ml-2" />
                            </InputAdornment>
                          }
                          endAdornment={
                            searchQuery ? (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="clear search"
                                  onClick={() => setSearchQuery('')}
                                  edge="end"
                                  size="small"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ) : null
                          }
                        />
                      </Box>

                      {/* Filter button */}
                      <Box className="filter-icon-container" onClick={toggleFilters}>
                        <img src={FilterListIcon} alt="Filter" />
                      </Box>
                    </Box>
                  </Box>

                  {/* Filters Section - Shows below header */}
                  {showFilters && (
                    <Box className="filters-section">
                      <Box className="filters-row">
                        <Box className="filter-item">
                          <Typography className="filter-label">Levels</Typography>
                          <Box className="filter-input-container">
                            <FormControl fullWidth>
                              <Select
                                value={selectedLevels}
                                onChange={e => {
                                  console.log('Level filter changed:', e.target.value);
                                  setSelectedLevels(e.target.value);
                                }}
                                displayEmpty
                                className="filter-select"
                                IconComponent={() => (
                                  <img
                                    src={DropdownIcon}
                                    alt="dropdown"
                                    className="dropdown-icon"
                                  />
                                )}
                                MenuProps={MenuProps}
                                renderValue={selected => {
                                  if (!selected) return 'All';
                                  return selected
                                    .split('_')
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ');
                                }}
                              >
                                <MenuItem value="" className="menu-item">
                                  <Typography>All</Typography>
                                  <Radio checked={selectedLevels === ''} />
                                </MenuItem>
                                {/* Map through level options based on teacher type */}
                                {LEVEL_OPTIONS[teacherProfile?.teacher_type || 'academic'].map(
                                  option => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                      className="menu-item"
                                    >
                                      <Typography>{option.label}</Typography>
                                      <Radio checked={selectedLevels === option.value} />
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>

                        <Box className="filter-item">
                          <Typography className="filter-label">Session Type</Typography>
                          <Box className="filter-input-container">
                            <FormControl fullWidth>
                              <Select
                                value={selectedSessionTypes}
                                onChange={e => {
                                  console.log('Session Type filter changed:', e.target.value);
                                  setSelectedSessionTypes(e.target.value);
                                }}
                                displayEmpty
                                className="filter-select"
                                IconComponent={() => (
                                  <img
                                    src={DropdownIcon}
                                    alt="dropdown"
                                    className="dropdown-icon"
                                  />
                                )}
                                MenuProps={MenuProps}
                                renderValue={selected => {
                                  if (!selected) return 'All';
                                  return selected
                                    .split('_')
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ');
                                }}
                              >
                                <MenuItem value="" className="menu-item">
                                  <Typography>All</Typography>
                                  <Radio checked={selectedSessionTypes === ''} />
                                </MenuItem>
                                {/* Map through session type options */}
                                {SESSION_TYPE_OPTIONS.map(option => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                    className="menu-item"
                                  >
                                    <Typography>{option.label}</Typography>
                                    <Radio checked={selectedSessionTypes === option.value} />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>

                        <Box className="filter-item">
                          <Typography className="filter-label">Subjects</Typography>
                          <Box className="filter-input-container">
                            <FormControl fullWidth>
                              <Select
                                value={selectedSubjects}
                                onChange={e => {
                                  const selectedSubject = subjectsList.find(
                                    subject => subject.title === e.target.value
                                  );
                                  setSelectedSubjects(e.target.value);
                                  // Clear selected tags when subject changes
                                  setSelectedTags('');
                                  if (selectedSubject) {
                                    // Reset and fetch first page of tags for the chosen subject
                                    setTagsList([]);
                                    setTagsTotalCount(0);
                                    setTagsPage(1);
                                    getTags(selectedSubject.id, 1, TAGS_PAGE_SIZE, false);
                                  } else {
                                    setTagsList([]);
                                  }
                                }}
                                displayEmpty
                                className="filter-select"
                                IconComponent={() => (
                                  <img
                                    src={DropdownIcon}
                                    alt="dropdown"
                                    className="dropdown-icon"
                                  />
                                )}
                                MenuProps={{
                                  ...MenuProps,
                                  MenuListProps: { onScroll: handleSubjectsMenuScroll },
                                }}
                                onOpen={() => {
                                  if (subjectsList.length === 0 && teacherProfile?.teacher_type) {
                                    getSubjects(
                                      teacherProfile.teacher_type,
                                      1,
                                      SUBJECTS_PAGE_SIZE,
                                      false
                                    );
                                  }
                                }}
                                renderValue={selected => selected || 'All'}
                              >
                                <MenuItem value="" sx={{ justifyContent: 'space-between' }}>
                                  <Typography>All</Typography>
                                  <Radio
                                    checked={selectedSubjects === ''}
                                    size="small"
                                    sx={{
                                      padding: 0,
                                      '& .MuiSvgIcon-root': {
                                        fontSize: 20,
                                      },
                                    }}
                                  />
                                </MenuItem>
                                {subjectsList.map(subject => (
                                  <MenuItem
                                    key={subject.id}
                                    value={subject.title}
                                    sx={{ justifyContent: 'space-between' }}
                                  >
                                    <Typography>{subject.title}</Typography>
                                    <Radio
                                      checked={selectedSubjects === subject.title}
                                      size="small"
                                      sx={{
                                        padding: 0,
                                        '& .MuiSvgIcon-root': {
                                          fontSize: 20,
                                        },
                                      }}
                                    />
                                  </MenuItem>
                                ))}
                                {subjectsLoading && (
                                  <MenuItem disabled>
                                    <Typography variant="body2" color="text.secondary">
                                      Loading...
                                    </Typography>
                                  </MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>

                        {selectedSubjects && (
                          <Box className="filter-item">
                            <Typography className="filter-label">Tags</Typography>
                            <Box className="filter-input-container">
                              <FormControl fullWidth>
                                <Select
                                  value={selectedTags}
                                  onChange={e => {
                                    const value = e.target.value;
                                    setSelectedTags(value);
                                  }}
                                  displayEmpty
                                  className="filter-select"
                                  IconComponent={() => (
                                    <img
                                      src={DropdownIcon}
                                      alt="dropdown"
                                      className="dropdown-icon"
                                    />
                                  )}
                                  MenuProps={{
                                    ...MenuProps,
                                    MenuListProps: { onScroll: handleTagsMenuScroll },
                                  }}
                                  onOpen={() => {
                                    if (tagsList.length === 0 && selectedSubjects) {
                                      const selectedSubject = subjectsList.find(
                                        subject => subject.title === selectedSubjects
                                      );
                                      if (selectedSubject?.id) {
                                        getTags(selectedSubject.id, 1, TAGS_PAGE_SIZE, false);
                                      }
                                    }
                                  }}
                                  renderValue={selected => selected || 'All'}
                                >
                                  <MenuItem value="" sx={{ justifyContent: 'space-between' }}>
                                    <Typography>All</Typography>
                                    <Radio
                                      checked={!selectedTags}
                                      size="small"
                                      sx={{
                                        padding: 0,
                                        '& .MuiSvgIcon-root': {
                                          fontSize: 20,
                                          border: '2px solid #D5DEE3',
                                          borderRadius: '50%',
                                          backgroundColor: 'white',
                                          '&.Mui-checked': {
                                            border: '2px solid #0A5EF3',
                                            backgroundColor: 'white',
                                            '&:after': {
                                              content: '""',
                                              position: 'absolute',
                                              top: '50%',
                                              left: '50%',
                                              transform: 'translate(-50%, -50%)',
                                              width: '10px',
                                              height: '10px',
                                              backgroundColor: '#0A5EF3',
                                              borderRadius: '50%',
                                            },
                                          },
                                        },
                                      }}
                                    />
                                  </MenuItem>
                                  {tagsList.map(tag => (
                                    <MenuItem
                                      key={tag.id}
                                      value={tag.title}
                                      sx={{ justifyContent: 'space-between' }}
                                    >
                                      <Typography>{tag.title}</Typography>
                                      <Radio
                                        checked={selectedTags === tag.title}
                                        size="small"
                                        sx={{
                                          padding: 0,
                                          '& .MuiSvgIcon-root': {
                                            fontSize: 20,
                                            border: '2px solid #D5DEE3',
                                            borderRadius: '50%',
                                            backgroundColor: 'white',
                                            '&.Mui-checked': {
                                              border: '2px solid #0A5EF3',
                                              backgroundColor: 'white',
                                              '&:after': {
                                                content: '""',
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                width: '10px',
                                                height: '10px',
                                                backgroundColor: '#0A5EF3',
                                                borderRadius: '50%',
                                              },
                                            },
                                          },
                                        }}
                                      />
                                    </MenuItem>
                                  ))}
                                  {tagsLoading && (
                                    <MenuItem disabled>
                                      <Typography variant="body2" color="text.secondary">
                                        Loading...
                                      </Typography>
                                    </MenuItem>
                                  )}
                                </Select>
                              </FormControl>
                            </Box>
                            {/* Single select: no chips display */}
                          </Box>
                        )}
                      </Box>

                      {/* Clear All button below filters */}
                      <Box className="sessions-clear-filters-container">
                        {(selectedSubjects ||
                          selectedLevels ||
                          selectedSessionTypes ||
                          selectedTags ||
                          selectedDate ||
                          !isDefaultMonth) && (
                          <Typography
                            className="sessions-clear-filters-button"
                            onClick={clearFilters}
                          >
                            Clear all filters
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box className="session-table-container">
                  <Box sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        background: '#FFF',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingRight: '16px',
                      }}
                    >
                      <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                        <Tab
                          label={translate('session', 'upcomingTabText')}
                          aria-label="all tab"
                          sx={{ textTransform: 'none' }}
                          {...a11yProps(0)}
                        />
                        <Tab
                          label={translate('session', 'completedTabText')}
                          aria-label="pending tab"
                          sx={{ textTransform: 'none' }}
                          {...a11yProps(1)}
                        />
                        <Tab
                          label={translate('session', 'cancelledTabText')}
                          sx={{ textTransform: 'none' }}
                          {...a11yProps(2)}
                        />
                      </Tabs>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size="small" onClick={handlePrevMonth}>
                          <ArrowBackIosNewIcon fontSize="small" />
                        </IconButton>

                        <IconButton size="small" onClick={handleCalendarClick} color="primary">
                          <img src={CalendarMonthIcon} alt="calendar" />
                        </IconButton>

                        <Typography variant="body2" color="text.secondary">
                          {getCurrentMonthYear()}
                        </Typography>

                        <IconButton size="small" onClick={handleNextMonth}>
                          <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>

                        <Popover
                          open={openCalendar}
                          anchorEl={calendarAnchorEl}
                          onClose={handleCalendarClose}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                          PaperProps={{
                            className: 'calendar-paper',
                          }}
                        >
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateCalendar
                              value={selectedDate ? dayjs(selectedDate) : currentMonth}
                              onChange={handleDateChange}
                              views={['day']}
                              defaultCalendarMonth={currentMonth}
                            />
                          </LocalizationProvider>
                        </Popover>
                      </Box>
                    </Box>

                    <CustomTabPanel value={value} index={0}>
                      <SessionTable sessionData={upcomingSessionData} />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={1}>
                      <SessionTable sessionData={completedSessionData} />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={2}>
                      <SessionTable sessionData={cancelledSessionData} />
                    </CustomTabPanel>
                  </Box>
                </Box>

                {/* Pagination UI */}
                <Box className="session-pagination-container">
                  <Box className="session-pagination-info">
                    <Typography className="session-pagination-text">Showing</Typography>
                    <Select
                      value={currentPagination.limit}
                      onChange={e => handleRowsPerPageChange(e.target.value, currentPagination)}
                      className="session-pagination-select"
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={15}>15</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={30}>30</MenuItem>
                    </Select>
                    <Typography className="session-pagination-text">
                      of {currentPagination.count} sessions
                    </Typography>
                  </Box>
                  <nav>
                    <List>
                      {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                        let children = null;

                        if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                          children = '…';
                        } else if (type === 'page') {
                          children = (
                            <button
                              type="button"
                              className={`session-pagination-button ${selected ? 'session-pagination-button-selected' : ''}`}
                              {...item}
                            >
                              {itemPage}
                            </button>
                          );
                        } else {
                          children = (
                            <button
                              type="button"
                              className="session-pagination-button session-pagination-button-nav"
                              {...item}
                            >
                              {formatText(type)}
                            </button>
                          );
                        }

                        return <li key={index}>{children}</li>;
                      })}
                    </List>
                  </nav>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Sessions;
